'use client';

import { useFestivalContext } from '@/contexts/FestivalContext';
import { withBasePathForAssets } from '@/utils/base-path';
import { cn } from '@/utils/tailwind-merge';
import { CSSProperties, forwardRef, HTMLAttributes } from 'react';

type SpanAttributes = Omit<HTMLAttributes<HTMLSpanElement>, 'color'>;

export interface ThemedIconProps extends SpanAttributes {
    /** Path to the SVG icon located under /public */
    src: string;
    /** CSS color value or token to apply. Defaults to current text color. */
    color?: string;
    /** Convenience shortcut to set both width and height in pixels. */
    size?: number;
    width?: number;
    height?: number;
}

const ORIGIN =
    process.env.NODE_ENV === 'production'
        ? (process.env.NEXT_PUBLIC_ASSET_ORIGIN ??
          'https://image-nations-long-film-festivale.fly.dev')
        : '';

const ThemedIcon = forwardRef<HTMLSpanElement, ThemedIconProps>(function ThemedIcon(
    {
        src,
        color = 'currentColor',
        size,
        width,
        height,
        className,
        style,
        role,
        'aria-label': ariaLabel,
        ...rest
    },
    ref,
) {
    const { basePath } = useFestivalContext();
    const iconUrl = withBasePathForAssets(src, basePath);

    const dimensionStyle: CSSProperties =
        typeof size === 'number'
            ? { width: size, height: size }
            : {
                  ...(typeof width === 'number' ? { width } : {}),
                  ...(typeof height === 'number' ? { height } : {}),
              };

    const maskValue = `url("${ORIGIN}${src}") center / contain no-repeat`;
    const finalRole = ariaLabel ? (role ?? 'img') : 'presentation';

    return (
        <span
            ref={ref}
            {...rest}
            role={finalRole}
            aria-label={ariaLabel}
            aria-hidden={ariaLabel ? undefined : true}
            className={cn('inline-block align-middle', className)}
            style={{
                display: 'inline-block',
                backgroundColor: color,
                WebkitMask: maskValue,
                mask: maskValue,
                ...dimensionStyle,
                ...style,
            }}
        />
    );
});

export default ThemedIcon;
